import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Users, ArrowLeft, Save, KeyRound, AlertTriangle, CheckCircle, XCircle, X, Copy, Check } from 'lucide-react';
import api from '../../services/api';
import { GlassButton } from '../../components/ui/GlassButton';

interface UserDetailData {
    id: string | number;
    full_name?: string;
    display_id?: string;
    phone?: string;
    email?: string;
    is_active: boolean;
    bots_count?: number;
    bots?: any[];
}

export const UserDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isEditMode = Boolean(location.state?.edit);
    const [user, setUser] = useState<UserDetailData | null>(location.state?.user ?? null);
    const [loading, setLoading] = useState(!location.state?.user);
    const [editForm, setEditForm] = useState({ full_name: '', is_active: true });
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [resettingPw, setResettingPw] = useState(false);
    const [pwConfirmStep, setPwConfirmStep] = useState<'idle' | 'confirm' | 'loading' | 'success' | 'error'>('idle');
    const [pwResult, setPwResult] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (location.state?.user) {
            const u = location.state.user;
            setUser(u);
            setEditForm({ full_name: (u as any).full_name ?? '', is_active: (u as any).is_active !== false });
            return;
        }
        if (!id) return;
        const load = async () => {
            try {
                setLoading(true);
                const res = await api.get('/banana/users');
                const list = res.data?.users ?? [];
                const found = list.find((u: any) => String(u.id) === String(id));
                if (found) {
                    setUser(found);
                    setEditForm({ full_name: found.full_name ?? '', is_active: found.is_active !== false });
                } else {
                    setUser(null);
                }
            } catch (e) {
                console.error('Erro ao carregar usuário:', e);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, location.state?.user]);

    const handleSave = async () => {
        if (!id) return;
        setSaving(true);
        setToast(null);
        try {
            await api.patch(`/banana/users/${id}`, { full_name: editForm.full_name.trim() || undefined });
            await api.patch(`/banana/users/${id}/status`, { is_active: editForm.is_active });
            setUser(prev => prev ? { ...prev, full_name: editForm.full_name.trim() || prev.full_name, is_active: editForm.is_active } : null);
            setToast({ msg: 'Alterações salvas.', type: 'success' });
        } catch (e: any) {
            setToast({ msg: e.response?.data?.error || 'Erro ao salvar.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-white opacity-50">Carregando detalhes do usuário...</div>
        );
    }

    if (!user) {
        return (
            <div className="p-6">
                <button
                    type="button"
                    onClick={() => navigate('/banana')}
                    className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-6"
                >
                    <ArrowLeft size={18} /> Voltar
                </button>
                <p className="text-gray-500">Usuário não encontrado.</p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-2xl">
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-8"
            >
                <ArrowLeft size={18} /> Voltar
            </button>

            {toast && (
                <div className={`mb-4 px-4 py-2 rounded-xl text-sm ${toast.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {toast.msg}
                </div>
            )}

            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2 mb-8">
                    <Users size={28} className="text-orange-500" />
                    {isEditMode ? 'Editar usuário' : 'Detalhes do usuário'}
                </h1>

                {isEditMode ? (
                    <div className="space-y-6">
                        <div>
                            <label className="text-[10px] text-gray-500 font-bold uppercase mb-1 block">Nome</label>
                            <input
                                type="text"
                                value={editForm.full_name}
                                onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:border-orange-500/50 outline-none"
                                placeholder="Nome do usuário"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-gray-500 font-bold uppercase mb-1 block">ID / Contato</label>
                            <p className="text-gray-400 font-mono text-sm break-all">{user.display_id ?? user.phone ?? user.email ?? '—'}</p>
                        </div>
                        <div>
                            <label className="text-[10px] text-gray-500 font-bold uppercase mb-1 block">Status</label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={editForm.is_active}
                                    onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))}
                                    className="rounded border-white/20 bg-white/5 text-orange-500 focus:ring-orange-500/50"
                                />
                                <span className="text-white text-sm">Usuário ativo</span>
                            </label>
                            <p className="text-[10px] text-gray-500 mt-1">Desmarque para bloquear o usuário.</p>
                        </div>
                        <div>
                            <span className="text-[10px] text-gray-500 font-bold uppercase">Bots vinculados</span>
                            <p className="text-white mt-1">{user.bots_count ?? user.bots?.length ?? 0} bots</p>
                        </div>
                        <div className="flex gap-3 pt-4">
                            <GlassButton onClick={handleSave} loading={saving} disabled={saving}>
                                {saving ? null : <Save size={16} />}
                                Salvar alterações
                            </GlassButton>
                            <button
                                type="button"
                                onClick={() => navigate(`/banana/usuarios/${id}`, { state: { user } })}
                                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
                            >
                                Ver apenas detalhes
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <dl className="space-y-6 text-sm">
                            <div>
                                <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Nome</dt>
                                <dd className="text-white text-lg">{user.full_name ?? '—'}</dd>
                            </div>
                            <div>
                                <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">ID / Contato</dt>
                                <dd className="text-gray-300 font-mono text-sm break-all">{user.display_id ?? user.phone ?? user.email ?? '—'}</dd>
                            </div>
                            <div>
                                <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Status</dt>
                                <dd>
                                    <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold ${user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                        {user.is_active ? 'ATIVO' : 'BLOQUEADO'}
                                    </span>
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Bots vinculados</dt>
                                <dd className="text-white">{user.bots_count ?? user.bots?.length ?? 0} bots</dd>
                            </div>
                        </dl>

                        <p className="mt-8 text-xs text-gray-500">
                            Para editar nome e status, use <strong className="text-gray-400">Editar usuário</strong> no menu de ações (3 pontos) na lista.
                        </p>

                        {pwConfirmStep !== 'idle' && (
                            <div className="mt-6 border border-white/10 rounded-2xl bg-white/5 overflow-hidden">
                                {pwConfirmStep === 'confirm' && (
                                    <div className="p-5">
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5 p-2 rounded-lg bg-amber-500/10">
                                                <AlertTriangle size={18} className="text-amber-400" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-white text-sm font-semibold">Redefinir senha</p>
                                                <p className="text-gray-400 text-xs mt-1">Uma nova senha temporária será gerada para este usuário.</p>
                                            </div>
                                            <button type="button" onClick={() => setPwConfirmStep('idle')} className="text-gray-500 hover:text-white transition-colors">
                                                <X size={16} />
                                            </button>
                                        </div>
                                        <div className="flex gap-2 mt-4">
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    setPwConfirmStep('loading');
                                                    try {
                                                        const res = await api.post(`/banana/reset-user-password/${id}`);
                                                        setPwResult(res.data.tempPassword || '');
                                                        setPwConfirmStep('success');
                                                    } catch (err: any) {
                                                        setPwResult(err.response?.data?.error || 'Erro ao redefinir senha.');
                                                        setPwConfirmStep('error');
                                                    }
                                                }}
                                                className="flex-1 py-2 rounded-lg bg-gradient-to-r from-orange-600 to-amber-500 text-white text-xs font-semibold transition-all hover:from-orange-500 hover:to-amber-400"
                                            >
                                                Confirmar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPwConfirmStep('idle')}
                                                className="flex-1 py-2 rounded-lg border border-white/10 bg-white/5 text-gray-400 text-xs font-medium hover:bg-white/10 hover:text-white transition-all"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {pwConfirmStep === 'loading' && (
                                    <div className="p-5 flex items-center gap-3">
                                        <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                                        <span className="text-gray-400 text-sm">Redefinindo senha...</span>
                                    </div>
                                )}
                                {pwConfirmStep === 'success' && (
                                    <div className="p-5">
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5 p-2 rounded-lg bg-green-500/10">
                                                <CheckCircle size={18} className="text-green-400" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-green-400 text-sm font-semibold">Senha redefinida</p>
                                                {pwResult && (
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <code className="bg-black/60 border border-white/10 rounded-lg px-3 py-1.5 text-orange-400 font-mono text-sm tracking-wider">{pwResult}</code>
                                                        <button
                                                            type="button"
                                                            onClick={() => { navigator.clipboard.writeText(pwResult); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                                                            className="p-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                                                        >
                                                            {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setPwConfirmStep('idle'); setPwResult(''); setCopied(false); }}
                                            className="mt-3 w-full py-2 rounded-lg border border-white/10 bg-white/5 text-gray-400 text-xs font-medium hover:bg-white/10 hover:text-white transition-all"
                                        >
                                            Fechar
                                        </button>
                                    </div>
                                )}
                                {pwConfirmStep === 'error' && (
                                    <div className="p-5">
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5 p-2 rounded-lg bg-red-500/10">
                                                <XCircle size={18} className="text-red-400" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-red-400 text-sm font-semibold">Erro</p>
                                                <p className="text-gray-400 text-xs mt-1">{pwResult}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setPwConfirmStep('idle'); setPwResult(''); }}
                                            className="mt-3 w-full py-2 rounded-lg border border-white/10 bg-white/5 text-gray-400 text-xs font-medium hover:bg-white/10 hover:text-white transition-all"
                                        >
                                            Fechar
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => navigate(`/banana/usuarios/${id}`, { state: { user, edit: true } })}
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white text-sm font-semibold transition-all"
                            >
                                <Save size={16} /> Usuário
                            </button>
                            <button
                                type="button"
                                onClick={() => setPwConfirmStep('confirm')}
                                disabled={pwConfirmStep === 'loading'}
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 text-sm font-semibold transition-all disabled:opacity-50"
                            >
                                <KeyRound size={16} /> Senha
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/banana/usuarios')}
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white text-sm font-medium transition-all"
                            >
                                <Users size={16} /> Gestão
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white text-sm font-medium transition-all"
                            >
                                <ArrowLeft size={16} /> Voltar
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
